'use client';

import { useState } from 'react';
import AppImage from '@/components/Image';
import { ParentTextbookTocLinks } from '@/components/parent/ParentTextbookTocLinks';
import type { TextbookTocData } from '@/lib/textbook-toc';
import type { ParentSearchItem } from '@/types/parent-search';

function toTitleCaseLabel(value: string | undefined | null): string {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

type TextbookFeaturedSectionProps = {
  item: ParentSearchItem;
  toc: TextbookTocData;
  onOpen: (item: ParentSearchItem) => void;
};

export function TextbookFeaturedSection({ item, toc, onOpen }: TextbookFeaturedSectionProps) {
  const firstChapterId = toc.chapters[0]?.id ?? null;
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(
    new Set(firstChapterId ? [firstChapterId] : []),
  );

  const title = item.bookName || item.title;
  const metaLine = [toTitleCaseLabel(item.subject), item.defaultGrade].filter(Boolean).join(' · ');
  const totalLessons = toc.chapters.reduce((sum, ch) => sum + ch.lessons.length, 0);

  function toggleChapter(id: string) {
    setExpandedChapters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  return (
    <article className="rounded-[22px] border border-[#D8E3F0] bg-[#F2F6FB] p-5">
      {/* Header: cover + info */}
      <div className="flex gap-5">
        <div className="shrink-0">
          {item.coverImage ? (
            <div className="overflow-hidden rounded-[14px] shadow-[0_8px_16px_rgba(59,86,120,0.12)]">
              <AppImage
                src={item.coverImage}
                alt={title}
                width={88}
                height={120}
                className="h-[120px] w-[88px] object-cover"
                optimizedWidth={176}
              />
            </div>
          ) : (
            <div className="flex h-[120px] w-[88px] items-center justify-center rounded-[14px] bg-[linear-gradient(180deg,#EEF2FF_0%,#E2E8F0_100%)] px-2">
              <span className="line-clamp-4 text-center text-xs font-semibold text-[#334155]">{title}</span>
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h2 className="text-[17px] font-bold leading-snug text-[#1D2B3A]">{title}</h2>
          {metaLine ? <p className="mt-0.5 text-[13px] text-[#6F83A0]">{metaLine}</p> : null}
          <p className="mt-1 text-[13px] text-[#9AAEC0]">
            {toc.chapters.length} {toc.chapters.length === 1 ? 'chapter' : 'chapters'} &middot; {totalLessons}{' '}
            {totalLessons === 1 ? 'lesson' : 'lessons'}
          </p>
        </div>
      </div>

      {/* TOC */}
      <div className="mt-5 border-t border-[#D8E3F0] pt-4">
        <h3 className="mb-3 text-[12px] font-semibold uppercase tracking-wider text-[#8B9DB0]">
          Table of Contents
        </h3>
        <div className="space-y-1">
          {toc.chapters.map((chapter) => {
            const isExpanded = expandedChapters.has(chapter.id);
            return (
              <div key={chapter.id} className="overflow-hidden rounded-[12px]">
                <button
                  type="button"
                  onClick={() => toggleChapter(chapter.id)}
                  className="flex w-full items-center justify-between rounded-[12px] px-4 py-2.5 text-left hover:bg-[#EAF1F8]"
                >
                  <span className="text-[14px] font-semibold text-[#2B415D]">{chapter.title}</span>
                  <span className="ml-2 shrink-0 text-[11px] text-[#9AAEC0]">
                    {isExpanded ? '▾' : '▸'}
                  </span>
                </button>
                {isExpanded ? (
                  <div className="divide-y divide-[#EEF2F7] rounded-b-[12px] bg-white">
                    <ParentTextbookTocLinks chapter={chapter} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </article>
  );
}
